import React, { useEffect } from "react";
import DateRangePicker from "../common/DateRangePicker";
import { usePatients } from "../../hooks/usePatients";
import { useAuth } from "../../hooks/useAuth";

// Status options based on our business logic
const STATUS_OPTIONS = [
  { value: "", label: "All Reports" },
  { value: "bloodReport", label: "Blood Report" },
  { value: "dexaReport", label: "DEXA Report" },
  { value: "vo2maxReport", label: "VO2 Max Report" },
  { value: "fatLossReport", label: "Fat Loss Report" },
  { value: "alzheimerReport", label: "Alzheimer Report" },
  { value: "strengthReport", label: "Strength Report" },
  { value: "summaryReport", label: "Summary Report" },
];

// Payment status options
const PAYMENT_STATUS_OPTIONS = [
  { value: "", label: "All Payment Status" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
];

// Preset date options
const DATE_PRESETS = [
  { key: "lastWeek", label: "Last Week" },
  { key: "lastMonth", label: "Last Month" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "tomorrow", label: "Tomorrow" },
];

const FilterPanel = ({ filteredCount, hidePaymentStatus = false }) => {
  const {
    filters,
    updateFilters,
    loadBranches,
    loadPatients,
    branches,
    patients,
  } = usePatients();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const isSuperAdmin = currentUser?.role === "superadmin";
  
  useEffect(() => {
    loadBranches();
    
    // Set default to last 5 days on initial load
    if (!filters.startDate && !filters.endDate) {
      const today = new Date(); // Using the provided current date
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 7); // 6 days ago + today = 7 days

      updateFilters({
        startDate: startDate,
        endDate: today,
        paymentStatus: "paid"
      });
    }
    // eslint-disable-next-line
  }, []);

  const handleStartDateChange = (date) => updateFilters({ startDate: date });
  const handleEndDateChange = (date) => updateFilters({ endDate: date });
  const handleBranchChange = (e) => updateFilters({ branchId: e.target.value });
  
  // Modified status change handler to work locally
  const handleStatusChange = (e) => {
    const selectedStatus = e.target.value;
    updateFilters({ status: selectedStatus });
  };
  
  // Handle payment status change
  const handlePaymentStatusChange = (e) => {
    const selectedPaymentStatus = e.target.value;
    updateFilters({ paymentStatus: selectedPaymentStatus });
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    updateFilters({ searchTerm: e.target.value });
  };

  const handleApplyFilters = () => {
    // Only load patients if non-status filters have changed
    if (filters.startDate || filters.endDate || filters.branchId) {
      loadPatients();
    }
  };

  const handleResetFilters = () => {
    updateFilters({
      startDate: null,
      endDate: null,
      branchId: "",
      status: "",
      paymentStatus: "",
      searchTerm: "",
    });
  };

  // Handle date preset selection
  const handleDatePreset = (preset) => {
    const today = new Date();
    let startDate = null;
    let endDate = null;
    
    switch (preset) {
      case "today":
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case "tomorrow":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() + 1);
        endDate = new Date(startDate);
        break;
      case "next3Days":
        startDate = new Date(today);
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 3);
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        endDate = new Date(startDate);
        break;
      case "lastWeek":
        endDate = new Date(today);
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "lastMonth":
        endDate = new Date(today);
        startDate = new Date(today);
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      default:
        break;
    }
    
    updateFilters({ startDate, endDate });
  };

  const branchOptions = branches.map((branch) => ({
    value: branch.id,
    label: branch.name || branch.branch_name,
  }));

  // Custom styles for consistent alignment
  const customStyles = `
    /* Date picker custom styling */
    .react-datepicker-wrapper {
      width: 100%;
    }
    
    .react-datepicker__input-container input {
      height: 38px;
      padding: 0.5rem 0.75rem;
      border: 1px solid #e2e8f0;
      border-radius: 0.25rem;
      width: 100%;
      font-size: 0.875rem;
      line-height: 1.5;
      box-sizing: border-box;
      outline: none;
      background-color: #fff;
    }
    
    /* Select dropdown styling - pull arrow inside */
    select {
      appearance: none;
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 8px center;
      background-size: 20px;
      padding-right: 32px;
    }
    
    /* Preset button styling */
    .preset-btn {
      font-size: 0.875rem;
      padding: 0.375rem 0.75rem;
      border-radius: 0.25rem;
      background-color: #f3f4f6;
      transition: all 0.2s;
    }
    
    .preset-btn:hover {
      background-color: #e5e7eb;
    }
    
    .preset-btn.active {
      background-color: #e2e8f0;
      font-weight: 500;
    }
  `;
  
  // Determine which preset is active
  const getPresetActive = () => {
    if (!filters.startDate || !filters.endDate) return null;
    
    const today = new Date(); // Using the provided current date
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    
    const startDate = new Date(filters.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(filters.endDate);
    endDate.setHours(0, 0, 0, 0);
    
    // Check if today
    if (startDate.getTime() === today.getTime() && endDate.getTime() === today.getTime()) {
      return "today";
    }
    
    // Check if tomorrow
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (startDate.getTime() === tomorrow.getTime() && endDate.getTime() === tomorrow.getTime()) {
      return "tomorrow";
    }
    
    // Check if next 3 days
    const next3Days = new Date(today);
    next3Days.setDate(next3Days.getDate() + 3);
    if (startDate.getTime() === today.getTime() && endDate.getTime() === next3Days.getTime()) {
      return "next3Days";
    }
    
    // Check if yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (startDate.getTime() === yesterday.getTime() && endDate.getTime() === yesterday.getTime()) {
      return "yesterday";
    }
    
    // Check if last week
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    if (startDate.getTime() === lastWeek.getTime() && endDate.getTime() === today.getTime()) {
      return "lastWeek";
    }

    // Check if last month
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    if (startDate.getTime() === lastMonth.getTime() && endDate.getTime() === today.getTime()) {
      return "lastMonth";
    }
    
    return null;
  };
  
  const activePreset = getPresetActive();

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <div className="text-2xl font-bold">
        {/* <h1 className="text-2xl font-bold">Patients</h1> */}
            Patients: <span className="text-orange-600 font-semibold">{typeof filteredCount !== 'undefined' ? filteredCount : (patients?.length || 0)}</span>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="Search patients..."
              value={filters.searchTerm || ''}
              onChange={handleSearchChange}
              className="h-[38px] pl-8 pr-3 py-2 border border-gray-200 rounded text-sm w-[250px]"
            />
            <svg
              className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
        </div>
        <div className="flex gap-3">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              style={{ backgroundColor: activePreset === preset.key ? '#f67006' : '#e2e8f0', color: activePreset === preset.key ? '#fff' : '#000' }}
              onClick={() => handleDatePreset(preset.key)}
              className={`preset-btn ${activePreset === preset.key ? 'active' : ''}`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Add custom CSS for consistent styling */}
      <style>{customStyles}</style>
      
      {/* Main filter container - using grid for perfect alignment */}
      <div className="grid grid-cols-12 gap-4 items-center">
        {/* Date pickers - span 3 columns */}
        <div className="col-span-12 md:col-span-3 flex gap-4" style={{ marginBottom: '-15px' }}>
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            className="w-full flex gap-4"
          />
        </div>
        
        {/* Branch dropdown - span 2.4 columns (only show for superadmin) */}
        {isSuperAdmin && (
          <div className="col-span-6 md:col-span-2">
            <select
              value={filters.branchId}
              onChange={handleBranchChange}
              className="w-full h-[38px] border border-gray-200 rounded px-3 text-sm"
            >
              <option value="">All Branches</option>
              {branchOptions.sort((a, b) => a.label.localeCompare(b.label)).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label.replace('VITAL INSIGHTS-', '').split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Status dropdown with local filtering */}
        <div className="col-span-6 md:col-span-2">
          <select
            value={filters.status || ""}
            onChange={handleStatusChange}
            className="w-full h-[38px] border border-gray-200 rounded px-3 text-sm"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Payment Status dropdown - hidden on Appointments page */}
        {!hidePaymentStatus && (
          <div className="col-span-6 md:col-span-2">
            <select
              value={filters.paymentStatus || ""}
              onChange={handlePaymentStatusChange}
              className="w-full h-[38px] border border-gray-200 rounded px-3 text-sm"
            >
              {PAYMENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Reset Button */}
        <div className="col-span-12 md:col-span-3 flex items-center gap-4 justify-end">
          <button
            type="button"
            onClick={handleResetFilters}
            className="bg-gray-400 text-white h-[38px] px-6 rounded hover:bg-gray-600 flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;